import React, { useContext, useState } from 'react'
import { Formik, useField, useFormikContext } from 'formik';
import * as yup from "yup";
import { Alert, Box, Button, Checkbox, Dialog, DialogContent, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Snackbar, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { AdminContext } from '../../../context/AdminContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import '../../../index.css';
import { formatDiscount } from '../../../global/formatter';

function CampaignDialog({ dialogOpen, handleClose }) {
    const { token } = useContext(AdminContext);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [alertText, setAlertText] = useState("");

    var initialValues = {
        campaign_title: "",
        code: "",
        bulk: false,
        coupon_count: 1,
        usage_limit: 1,
        heart: 0,
        gold: 0,
        premium: "none",
        for_subscription_discount: 0,
        discount_id: "",
        start_date: "",
        end_date: "",
    }

    var validationObject = {
        campaign_title: yup.string().required("required"),
        code: yup.string(),
        bulk: yup.boolean(),
        coupon_count: yup.number().min(1, "at least 1").required("required"),
        usage_limit: yup.number().min(1, "at least 1").required("required"),
        heart: yup.number().min(0, "at least 0").required("required"),
        gold: yup.number().min(0, "at least 0").required("required"),
        premium: yup.string().required("required"),
        for_subscription_discount: yup.number().min(0, "at least 0").required("required"),
        discount_id: yup.string().when("for_subscription_discount", {
            is: (val) => val > 0,
            then: () => yup.string().required("required"),
            otherwise: () => yup.string().nullable()
        }),
        start_date: yup.date().required("required"),
        end_date: yup.date().required("required")
    }

    const campaignSchema = yup.object().shape(validationObject);

    const handleFormSubmit = async (values) => {
        values.start_date = values.start_date.toISOString().split("Z")[0];
        values.end_date = values.end_date.toISOString().split("Z")[0];
        if (values.bulk) {
            await axios.post(`${process.env.REACT_APP_API_URL}coupon/createCouponsBulk?token=${token}&couponCount=${values.coupon_count}`, {
                ...values
            }).catch((err) => {
                console.log("err: " + err);
                if (err?.response && err.response.data.status === 402) {
                    setAlertText(err.response.data?.error?.message);
                    setOpenSnackbar(true);
                }
            }).then((response) => {
                if (response && response.data.success === true && response.data.status === 201) {
                    handleClose(true)
                }
            });
        } else {
            await axios.post(`${process.env.REACT_APP_API_URL}coupon/createCoupon?token=${token}`, {
                ...values
            }).catch((err) => {
                console.log("err: " + err);
                if (err?.response && err.response.data.status === 402) {
                    setAlertText(err.response.data?.error?.message);
                    setOpenSnackbar(true);
                }
            }).then((response) => {
                if (response && response.data.success === true && response.data.status === 201) {
                    handleClose(true)
                }
            });
        }

    }

    return (
        <Dialog
            sx={{ margin: "0 auto" }} open={dialogOpen}
            onClose={() => handleClose(false)}>
            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity="warning"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alertText}
                </Alert>
            </Snackbar>
            <Box mx="20px" mt="20px" textAlign="center">
                <Typography variant='h3'>
                    New Campaign
                </Typography>
            </Box>
            <DialogContent>
                <Box>
                    <Formik
                        onSubmit={handleFormSubmit}
                        initialValues={initialValues}
                        validationSchema={campaignSchema}
                    >
                        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
                            <form onSubmit={handleSubmit}>
                                <Box display="grid" gap="15px" gridTemplateColumns="repeat(4, minmax(0, 1fr))" mx="75px" mb="25px">
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Title"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        name={"campaign_title"}
                                        value={values.campaign_title}
                                        error={!!touched.campaign_title && !!errors.campaign_title}
                                        helperText={touched.campaign_title && errors.campaign_title}
                                        sx={{ gridColumn: "span 4" }}
                                    />
                                    <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2", marginTop: "10px" }}>
                                        <InputLabel id="filter-label">Type</InputLabel>
                                        <Select
                                            labelId="filter-label"
                                            label="Type"
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                                handleChange(e);
                                                if (e.target.value === 1) {
                                                    setFieldValue("for_subscription_discount", e.target.value);
                                                    setFieldValue("discount_id", "");
                                                    setFieldValue("heart", 0);
                                                    setFieldValue("gold", 0);
                                                    setFieldValue("premium", "none");

                                                } else {
                                                    setFieldValue("for_subscription_discount", e.target.value);
                                                    setFieldValue("discount_id", null);
                                                }
                                            }}
                                            name={"for_subscription_discount"}
                                            value={values.for_subscription_discount}
                                            error={!!touched.for_subscription_discount && !!errors.for_subscription_discount}
                                        >
                                            <MenuItem value={0}>General Campaign</MenuItem>
                                            <MenuItem value={1}>Discount Campaign</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {values.for_subscription_discount === 1 &&
                                        <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2", marginTop: "10px" }}>
                                            <InputLabel id="filter-label">Type</InputLabel>
                                            <Select
                                                labelId="filter-label"
                                                label="Type"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name={"discount_id"}
                                                value={values.discount_id}
                                                error={!!touched.discount_id && !!errors.discount_id}
                                            >
                                                <MenuItem value={"p1mtier1discount"}>{formatDiscount("p1mtier1discount")}</MenuItem>
                                                <MenuItem value={"p1mtier2discount"}>{formatDiscount("p1mtier2discount")}</MenuItem>
                                                <MenuItem value={"p1mtier3discount"}>{formatDiscount("p1mtier3discount")}</MenuItem>
                                                <MenuItem value={"p6mtier1discount"}>{formatDiscount("p6mtier1discount")}</MenuItem>
                                                <MenuItem value={"p6mtier2discount"}>{formatDiscount("p6mtier2discount")}</MenuItem>
                                                <MenuItem value={"p6mtier3discount"}>{formatDiscount("p6mtier3discount")}</MenuItem>
                                                <MenuItem value={"p6mtier4discount"}>{formatDiscount("p6mtier4discount")}</MenuItem>
                                                <MenuItem value={"p6mtier6discount"}>{formatDiscount("p6mtier6discount")}</MenuItem>
                                                <MenuItem value={"p1ytier1discount"}>{formatDiscount("p1ytier1discount")}</MenuItem>
                                                <MenuItem value={"p1ytier2discount"}>{formatDiscount("p1ytier2discount")}</MenuItem>
                                                <MenuItem value={"p1ytier3discount"}>{formatDiscount("p1ytier3discount")}</MenuItem>
                                                <MenuItem value={"p1ytier4discount"}>{formatDiscount("p1ytier4discount")}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    }
                                    {values.for_subscription_discount === 0 &&
                                        <>
                                            <Box sx={{ gridColumn: "span 2" }} />
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="number"
                                                label="Heart"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name={"heart"}
                                                value={values.heart}
                                                error={!!touched.heart && !!errors.heart}
                                                helperText={touched.heart && errors.heart}
                                                sx={{ gridColumn: "span 1" }}
                                            />
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="number"
                                                label="Gold"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name={"gold"}
                                                value={values.gold}
                                                error={!!touched.gold && !!errors.gold}
                                                helperText={touched.gold && errors.gold}
                                                sx={{ gridColumn: "span 1" }}
                                            />
                                            <FormControl fullWidth variant="filled">
                                                <InputLabel id="filter-label">Premium</InputLabel>
                                                <Select
                                                    labelId="filter-label"
                                                    label="Premium"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    name={"premium"}
                                                    value={values.premium}
                                                    sx={{ gridColumn: "span 1" }}
                                                >
                                                    <MenuItem value={"none"}>None</MenuItem>
                                                    <MenuItem value={"monthly"}>Monthly</MenuItem>
                                                    <MenuItem value={"six_month"}>6 Months</MenuItem>
                                                    <MenuItem value={"yearly"}>Yearly</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </>
                                    }
                                    <FormControlLabel
                                        control={<Checkbox
                                            checked={values.bulk}
                                            size="small"
                                        />}
                                        variant="filled"
                                        label="Generate Bulk Coupon Codes"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        name={"bulk"}
                                        value={values.bulk}
                                        sx={{ gridColumn: "span 4" }} />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Usage Limit per Coupon"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        name={"usage_limit"}
                                        value={values.usage_limit}
                                        error={!!touched.usage_limit && !!errors.usage_limit}
                                        helperText={touched.usage_limit && errors.usage_limit}
                                        sx={{ gridColumn: "span 2" }}
                                    />
                                    {values.bulk &&
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="number"
                                            label="Number of Coupons"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            name={"coupon_count"}
                                            value={values.coupon_count}
                                            error={!!touched.coupon_count && !!errors.coupon_count}
                                            helperText={touched.coupon_count && errors.coupon_count}
                                            sx={{ gridColumn: "span 2" }}
                                        />}
                                    {!values.bulk &&
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label={<Typography>Coupon Code (Leave blank if you want it to be auto-generated)</Typography>}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            name={"code"}
                                            value={values.code}
                                            error={!!touched.code && !!errors.code}
                                            helperText={touched.code && errors.code}
                                            sx={{ gridColumn: "span 4" }}
                                        />}
                                    <Box display="grid" gap="15px" rowGap={"5px"} gridTemplateColumns="repeat(4, minmax(0, 1fr))" gridColumn={"span 4"}>
                                        <Box sx={{ gridColumn: "span 2" }}>
                                            <InputLabel >Start Date</InputLabel>
                                            {!!errors?.start_date && <InputLabel sx={{ color: "#fc6565" }}>{errors.start_date}</InputLabel>}
                                        </Box>
                                        <Box sx={{ gridColumn: "span 2" }}>
                                            <InputLabel >End Date</InputLabel>
                                            {!!errors?.end_date && <InputLabel sx={{ color: "#fc6565" }}>{errors.end_date}</InputLabel>}
                                        </Box>
                                        <Box gridColumn={"span 2"}>
                                            <DatePickerField name="start_date" />
                                        </Box>
                                        <Box gridColumn={"span 2"}>
                                            <DatePickerField name="end_date" />
                                        </Box>
                                    </Box>

                                </Box>
                                <Box display="flex" justifyContent="end" m="20px 20px 0px 20px">
                                    <Button type="submit" color="secondary" variant="contained">
                                        <Typography variant='h5'>Save</Typography>
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog >
    )
}

const DatePickerField = ({ ...props }) => {
    const { setFieldValue, setFieldTouched, errors, touched } = useFormikContext();
    const [field] = useField(props);
    return (
        <DatePicker
            {...field}
            {...props}
            selected={(field.value && new Date(field.value)) || null}
            autoComplete="off"
            onChange={val => {
                console.log(val.toISOString().split("Z")[0]);
                setFieldValue(field.name, val);
                setFieldTouched(field.name);
            }}
        />
    );
};

export default CampaignDialog